import { unlockAccountingPeriodInputSchema, type AccountingPeriodView, type UnlockAccountingPeriodInput } from '@chukua/common';
import { Alert, Form, Input, Modal, Radio } from 'antd';

interface UnlockPeriodModalProps {
  period: AccountingPeriodView | null;
  error: string | null;
  submitting: boolean;
  onUnlock: (period: AccountingPeriodView, input: UnlockAccountingPeriodInput) => Promise<boolean>;
  onClose: () => void;
}

/** Unlocking is rare and leaves a trace, so it asks why. The reason is kept on the period's lock history and the audit trail. */
export function UnlockPeriodModal({ period, error, submitting, onUnlock, onClose }: UnlockPeriodModalProps) {
  const [form] = Form.useForm<UnlockAccountingPeriodInput>();

  async function submit(values: UnlockAccountingPeriodInput) {
    if (!period) return;
    const parsed = unlockAccountingPeriodInputSchema.safeParse(values);
    if (!parsed.success) return;
    if (await onUnlock(period, parsed.data)) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Modal
      open={period !== null}
      title="Unlock period"
      okText="Unlock"
      confirmLoading={submitting}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" initialValues={{ to: 'closing' }} onFinish={submit}>
        {period && (
          <p className="mb-4 text-sm text-muted">
            {period.startDate} to {period.endDate} stops refusing postings once it is unlocked.
          </p>
        )}
        {error && <Alert type="error" showIcon message={error} className="mb-4" />}
        <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why the period is being unlocked' }]}>
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
        <Form.Item name="to" label="Move it back to">
          <Radio.Group
            options={[
              { label: 'Closing', value: 'closing' },
              { label: 'Open', value: 'open' },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
